import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import { toApiErrorPayload } from "@/utils/apiErrorPayload";
import type {
  Unit,
  AreaType,
  RevenueType,
  OccupancyStatus,
  PrivateDetail,
  CommonDetail,
  FacilityDetail,
  LandDetail,
  BudgetInfo,
  StockRevenueInfo,
  ContractInfo,
  CustomFieldValue,
  UnitDocument,
  UnitImage,
} from "@ippon/shared-types";

const BaseURL = import.meta.env.VITE_API_URL;

interface UnitState {
  list: { data: Unit[]; loading: boolean; error: string | null };
  current: {
    data: Unit | null;
    loading: boolean;
    error: string | null;
    /** race ガード: 最後に fetch を要求した unit_id */
    requestedId: string | null;
  };
}

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const responseMessage = (error as any)?.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }
  return fallback;
};

const initialState: UnitState = {
  list: { data: [], loading: false, error: null },
  current: {
    data: null,
    loading: false,
    error: null,
    requestedId: null,
  },
};

// ── Thunks ────────────────────────────────────────────

interface ListUnitsParams {
  property_id: string;
  area_type?: AreaType;
  revenue_type?: RevenueType;
  keyword?: string;
}

export const fetchUnitsByProperty = createAsyncThunk<Unit[], ListUnitsParams>(
  "units/fetchByProperty",
  async (params, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/units`, { params });
      return res.data.data.items as Unit[];
    } catch {
      return rejectWithValue("区画一覧の取得に失敗しました");
    }
  },
);

/** レントロール用: 収益区画を抽出 (revenue_type=stock_rent 指定で BE GSI2 経由、段階1 は収益区画一括) */
export const fetchUnitsForRentRoll = createAsyncThunk<
  Unit[],
  { property_id: string }
>("units/fetchRentRoll", async ({ property_id }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/units`, {
      params: { property_id, revenue_type: "stock_rent" },
    });
    return res.data.data.items as Unit[];
  } catch {
    return rejectWithValue("レントロールの取得に失敗しました");
  }
});

export const fetchUnitById = createAsyncThunk<Unit, { unit_id: string }>(
  "units/fetchById",
  async ({ unit_id }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/units/${unit_id}`);
      return res.data.data as Unit;
    } catch {
      return rejectWithValue("区画の取得に失敗しました");
    }
  },
);

export interface CreateUnitBody {
  property_id: string;
  area_type: AreaType;
  landmark_name: string;
  occupancy_status?: OccupancyStatus;
  display_order?: number;
  is_parking?: boolean;
  revenue_type?: RevenueType;
  /** 収益項目 (付帯=stock_other のとき必須)。BudgetCategory.category_id */
  revenue_item_category_id?: string;
  /** 既存顧客から登録時に紐づける顧客 (Customer.customer_id)。BE 保存配線は Phase 2 で対応 */
  customer_id?: string;
  floor?: string;
  floor_plan?: string;
  notes?: string;
  private_detail?: PrivateDetail;
  common_detail?: CommonDetail;
  facility_detail?: FacilityDetail;
  land_detail?: LandDetail;
  budget?: BudgetInfo;
  revenue?: StockRevenueInfo;
  contract?: ContractInfo;
  custom_fields?: CustomFieldValue[];
  documents?: UnitDocument[];
  images?: UnitImage[];
}

export const createUnit = createAsyncThunk<Unit, CreateUnitBody>(
  "units/create",
  async (body, { rejectWithValue }) => {
    try {
      // UnitTabForm は legacy `contract: ContractInfo` (last_name/first_name 等) +
      // `budget: BudgetInfo` (start_date/expected_rent 等) で送ってくるが、
      // BE はもう Contract レコード作成用の新形式 (contractor_name / start_date / monthly_rent 等) を期待する。
      // ここで FE 内に閉じた形で形式変換する。
      const transformed = transformUnitBodyForCreate(body);
      const res = await ipponApi.post(`${BaseURL}/api/v1/units`, transformed);
      return res.data.data as Unit;
    } catch (error) {
      // field/code を保持して FE で input 下にインライン表示できるようにする
      // (名称重複等)。唯一の consumer は UnitCreateModal (applyApiError で振り分け)。
      return rejectWithValue(
        toApiErrorPayload(error, "区画の登録に失敗しました"),
      );
    }
  },
);

/**
 * legacy `contract: ContractInfo` + `budget: BudgetInfo` の値を
 * BE が期待する新 Contract 形式の `contract: { contractor_type, contractor_name, start_date, monthly_rent, ... }` に変換する。
 *
 * 契約者氏名 (last/first) と 開始日 が揃っている時のみ contract ブロックを送る。
 * 揃っていなければ Contract レコード発番は走らず、Unit のみ作成。
 */
function transformUnitBodyForCreate(
  body: CreateUnitBody,
): Record<string, unknown> {
  const contract = (body as any).contract as ContractInfo | undefined;
  const budget = body.budget;
  const startDate = budget?.start_date;
  if (!contract || !startDate) {
    // 契約データ無し: contract / budget の legacy フィールドは送らない
    const { contract: _c, ...rest } = body as any;
    void _c;
    return rest;
  }
  const isCorporate = contract.contractor_type === "corporate";
  const contractorName = isCorporate
    ? contract.company_name || ""
    : [(contract as any).last_name, (contract as any).first_name]
        .filter(Boolean)
        .join(" ");
  const contactPerson = isCorporate
    ? [
        (contract as any).representative_last_name,
        (contract as any).representative_first_name,
      ]
        .filter(Boolean)
        .join(" ") || undefined
    : undefined;
  if (!contractorName) {
    // 名前不明なら contract ブロックを送らない
    const { contract: _c, ...rest } = body as any;
    void _c;
    return rest;
  }
  const newContract: Record<string, unknown> = {
    contractor_type: contract.contractor_type,
    contractor_name: contractorName,
    ...(isCorporate && { company_name: contract.company_name || "" }),
    ...(contactPerson && { contact_person: contactPerson }),
    phone: contract.phone,
    email: contract.email,
    start_date: startDate,
    end_date: budget?.end_date,
    rent_start_date: budget?.rent_start_date,
    monthly_rent: budget?.expected_rent,
    rent_unit_price: budget?.rent_unit_price,
    maintenance_fee: budget?.common_fee,
    maintenance_fee_unit_price: budget?.maintenance_fee_unit_price,
    pricing_type: budget?.pricing_type,
    unit_price: budget?.unit_price,
    quantity: budget?.quantity,
    quantity_unit: budget?.quantity_unit,
    security_deposit: budget?.deposit_amount,
    security_deposit_after: budget?.deposit_after_amount,
    key_money: budget?.key_money_amount,
    contract_form: budget?.contract_form,
    contract_remarks: contract.note,
    vehicle: contract.vehicle,
  };
  // body.contract を新形式で上書き
  return { ...body, contract: newContract };
}

export interface UpdateUnitBody {
  area_type?: AreaType;
  landmark_name?: string;
  occupancy_status?: OccupancyStatus;
  display_order?: number;
  is_parking?: boolean;
  revenue_type?: RevenueType;
  /** 収益項目 (付帯=stock_other のとき必須)。null で明示クリア */
  revenue_item_category_id?: string | null;
  /** 紐づく顧客 (Customer.customer_id)。null で紐づけ解除 */
  customer_id?: string | null;
  floor?: string | null;
  floor_plan?: string | null;
  notes?: string | null;
  private_detail?: PrivateDetail | null;
  common_detail?: CommonDetail | null;
  facility_detail?: FacilityDetail | null;
  land_detail?: LandDetail | null;
  budget?: BudgetInfo | null;
  revenue?: StockRevenueInfo | null;
  contract?: ContractInfo | null;
  custom_fields?: CustomFieldValue[];
  documents?: UnitDocument[];
  images?: UnitImage[];
}

export const updateUnit = createAsyncThunk<
  Unit,
  { unit_id: string; body: UpdateUnitBody }
>("units/update", async ({ unit_id, body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/units/${unit_id}`,
      body,
    );
    return res.data.data as Unit;
  } catch (error) {
    return rejectWithValue(apiErrorMessage(error, "区画の更新に失敗しました"));
  }
});

export const deleteUnit = createAsyncThunk<string, { unit_id: string }>(
  "units/delete",
  async ({ unit_id }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(`${BaseURL}/api/v1/units/${unit_id}`);
      return unit_id;
    } catch {
      return rejectWithValue("区画の削除に失敗しました");
    }
  },
);

// ── Slice ─────────────────────────────────────────────

const unitSlice = createSlice({
  name: "units",
  initialState,
  reducers: {
    clearCurrent(state) {
      state.current.data = null;
      state.current.error = null;
      state.current.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnitsByProperty.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchUnitsByProperty.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchUnitsByProperty.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })
      .addCase(fetchUnitsForRentRoll.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchUnitsForRentRoll.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchUnitsForRentRoll.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })
      .addCase(fetchUnitById.pending, (state, action) => {
        const nextId = (action.meta.arg as any)?.unit_id || null;
        // 別 unit への遷移時は前データを必ずクリアして race を防ぐ
        if (state.current.requestedId !== nextId) {
          state.current.data = null;
        }
        state.current.requestedId = nextId;
        state.current.loading = true;
        state.current.error = null;
      })
      .addCase(fetchUnitById.fulfilled, (state, action) => {
        // 古い応答 (race) を弾く
        const requestedId = state.current.requestedId;
        const payloadId = (action.payload as any)?.unit_id;
        if (requestedId && payloadId && payloadId !== requestedId) {
          return;
        }
        state.current.loading = false;
        state.current.data = action.payload;
        state.current.error = null;
      })
      .addCase(fetchUnitById.rejected, (state, action) => {
        state.current.data = null;
        state.current.loading = false;
        state.current.error = action.payload as string;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        state.list.data.unshift(action.payload);
        // current.data は「いま詳細表示中のユニット」を指す共有スロット。
        // 新規作成でここを上書きすると、別ユニットの詳細プレビュー
        // (PreviewDrawer 等、背景操作可で同時表示されうる) が壊れて真っ白になる。
        // 作成直後に新ユニット詳細へ遷移する経路は無く (遷移先は fetchUnitById で
        // 取り直す)、ここで current を奪う必要がないため触らない。
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (u) => u.unit_id === action.payload.unit_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
        if (state.current.data?.unit_id === action.payload.unit_id) {
          state.current.data = action.payload;
        }
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (u) => u.unit_id !== action.payload,
        );
        if (state.current.data?.unit_id === action.payload) {
          state.current.data = null;
        }
      });
  },
});

export const { clearCurrent } = unitSlice.actions;
export default unitSlice.reducer;
